import { useMemo } from 'react';
import { Option } from '@common-fe/common-fe';

import { useDocumentOcrStore } from '@/modules/UseMyMoney/stores';
import { OcrProviderOption } from '@/modules/UseMyMoney/UseMyMoney.types';
import { getFullAddress } from '@/utils';

const MAX_ADDRESS_OPTIONS = 3;

interface Props {
  provider: OcrProviderOption;
}

const useAddressOptions = ({ provider }: Props) => {
  const { ocrDocument } = useDocumentOcrStore();

  const addressOptions = useMemo((): Option<string>[] => {
    const options = ocrDocument?.addressOptions?.slice(0, MAX_ADDRESS_OPTIONS)?.map((addressOption): Option<string> => {
      const addressTitle = getFullAddress(addressOption);

      const addressKeys = getFullAddress({
        line1: addressOption?.line1,
        city: addressOption?.city,
        state: {
          id: addressOption?.state?.id,
          code: addressOption?.state?.code,
          name: addressOption?.state?.name,
        },
        zipcode: addressOption?.zipcode,
      });

      return {
        key: addressKeys,
        title: addressTitle,
        value: addressTitle,
      };
    });
    return options || [];
  }, [ocrDocument?.addressOptions]);

  const providerAddressKey = useMemo(() => getFullAddress({
    line1: provider?.address?.line1,
    city: provider?.address?.city,
    state: {
      id: provider?.address?.state?.id,
      code: provider?.address?.state?.code,
      name: provider?.address?.state?.name,
    },
    zipcode: provider?.address?.zipcode,
  }), [provider?.address]);

  const activeAddressOption = useMemo(() => addressOptions?.find((addressOption) => addressOption?.key === providerAddressKey),
    [addressOptions, providerAddressKey]);

  return {
    addressOptions,
    activeAddressOption,
    providerAddressKey,
  };
};

export default useAddressOptions;
